import React, { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { supabase, TABLES } from '../supabase'
import toast from 'react-hot-toast'

const JudgePanel = () => {
  const { eventId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  
  // Try state first, fallback to localStorage
  const stateData = location.state || {}
  const [judgeId, setJudgeId] = useState(() => {
    return stateData.judgeId || localStorage.getItem(`judgeId_${eventId}`) || ''
  })
  const [judgeName, setJudgeName] = useState(() => {
    return stateData.judgeName || localStorage.getItem(`judgeName_${eventId}`) || ''
  })
  const [event, setEvent] = useState(() => {
    if (stateData.event) return stateData.event
    try {
      const saved = localStorage.getItem(`event_${eventId}`)
      return saved ? JSON.parse(saved) : null
    } catch (e) {
      return null
    }
  })

  // Synchronize localStorage when state is available
  useEffect(() => {
    const activeState = location.state || {}
    if (activeState.judgeId) {
      localStorage.setItem(`judgeId_${eventId}`, activeState.judgeId)
      setJudgeId(activeState.judgeId)
    }
    if (activeState.judgeName) {
      localStorage.setItem(`judgeName_${eventId}`, activeState.judgeName)
      setJudgeName(activeState.judgeName)
    }
    if (activeState.event) {
      localStorage.setItem(`event_${eventId}`, JSON.stringify(activeState.event))
      setEvent(activeState.event)
    }
  }, [location.state, eventId])

  // State management
  const [loading, setLoading] = useState(true)
  const [eventData, setEventData] = useState(null)
  const [criteria, setCriteria] = useState([])
  const [candidates, setCandidates] = useState([])
  const [specialAwards, setSpecialAwards] = useState([])
  
  // Scoring states
  const [criteriaScores, setCriteriaScores] = useState({}) // {candidateId-criteriaId: score}
  const [awardScores, setAwardScores] = useState({}) // {candidateId-awardId: score}
  
  // Completion tracking
  const [completedCriteria, setCompletedCriteria] = useState(new Set())
  const [completedAwards, setCompletedAwards] = useState(new Set())
  
  // UI states - only one can be expanded at a time
  const [expandedCriteria, setExpandedCriteria] = useState(null)
  const [expandedAwards, setExpandedAwards] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingSubmission, setPendingSubmission] = useState(null) // {type: 'criteria'|'award', id: string}
  
  // Lock states to prevent accidental changes while scrolling
  const [lockedScores, setLockedScores] = useState({}) // {candidateId-criteriaId or candidateId-awardId: true}
  
  // Column view preference (1, 2, or 3 columns)
  const [columnView, setColumnView] = useState(() => {
    const saved = localStorage.getItem('judgePanel_columnView')
    return saved ? parseInt(saved) : 3
  })
  
  // Show detailed scores review
  const [showDetailedReview, setShowDetailedReview] = useState(false)
  
  // Certification state
  const [isCertified, setIsCertified] = useState(false)
  const [certificationTimestamp, setCertificationTimestamp] = useState(null)
  const [certifying, setCertifying] = useState(false)
  
  // Recommendation notification state
  const [showRecommendation, setShowRecommendation] = useState(false)
  const [recommendedItem, setRecommendedItem] = useState(null) // {type: 'criteria'|'award', item: object}
  
  // Unified scoring sequence from admin
  const [scoringSequence, setScoringSequence] = useState([]) // [{type: 'criteria'|'award', id: string}, ...]
  
  // Score change request states
  const [changeRequests, setChangeRequests] = useState([]) // Existing change requests
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false)
  const [changeRequestData, setChangeRequestData] = useState(null) // {type: 'criteria'|'award', id: string, name: string}
  const [changeRequestReason, setChangeRequestReason] = useState('')
  const [requestingChange, setRequestingChange] = useState(false)
  
  // Help request states
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [helpMessage, setHelpMessage] = useState('')
  const [sendingHelp, setSendingHelp] = useState(false)

  // Scoring View Mode
  const [scoringMode, setScoringMode] = useState('criteria') // 'criteria' | 'candidate'
  const [expandedCandidate, setExpandedCandidate] = useState(null) // ID of expanded candidate in candidate view
  const [submittedCandidates, setSubmittedCandidates] = useState(new Set()) // Set of submitted candidate IDs

  // Check if a candidate's scores have been submitted
  const isCandidateSubmitted = (candidateId) => {
    return submittedCandidates.has(candidateId)
  }

  // Toggle candidate card expansion in candidate view
  const toggleCandidateExpansion = (candidateId) => {
    setExpandedCandidate(prev => prev === candidateId ? null : candidateId)
  }

  // Count locked scores for a candidate
  const getCandidateLockedCount = (candidateId) => {
    let count = 0
    const unlockedCriteria = criteria.filter(c => !c.is_locked)
    for (const criterion of unlockedCriteria) {
      if (lockedScores[`${candidateId}-${criterion.id}`] === true) {
        count++
      }
    }
    for (const award of specialAwards) {
      if (lockedScores[`${candidateId}-${award.id}`] === true) {
        count++
      }
    }
    return count
  }

  // Total tasks (scores) for a single candidate
  const getCandidateTotalTasks = () => {
    return criteria.filter(c => !c.is_locked).length + specialAwards.length
  }
  
  // Toggle lock for a specific score
  const toggleScoreLock = (key) => {
    setLockedScores(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }
  
  // Change column view and save preference
  const changeColumnView = (columns) => {
    setColumnView(columns)
    localStorage.setItem('judgePanel_columnView', columns.toString())
  }
  
  // Check if a score is locked
  const isScoreLocked = (key) => {
    return lockedScores[key] === true
  }

  // Get the next recommended item to score based on unified scoring sequence
  const getNextRecommendedItem = (justCompletedType, justCompletedId, newCompletedCriteria, newCompletedAwards) => {
    // Get unlocked criteria
    const unlockedCriteriaList = criteria.filter(c => !c.is_locked)
    
    // If there's a unified scoring sequence defined by admin, use it
    if (scoringSequence && scoringSequence.length > 0) {
      // Find the current item's position in the sequence
      const currentIndex = scoringSequence.findIndex(
        item => item.type === justCompletedType && item.id === justCompletedId
      )
      
      // Look for the next pending item in the sequence after the current position
      for (let i = currentIndex + 1; i < scoringSequence.length; i++) {
        const seqItem = scoringSequence[i]
        
        if (seqItem.type === 'criteria') {
          // Check if this criteria is unlocked and not completed
          const criteriaItem = unlockedCriteriaList.find(c => c.id === seqItem.id)
          if (criteriaItem && !newCompletedCriteria.has(seqItem.id)) {
            return { type: 'criteria', item: criteriaItem }
          }
        } else if (seqItem.type === 'award') {
          // Check if this award is not completed
          const awardItem = specialAwards.find(a => a.id === seqItem.id)
          if (awardItem && !newCompletedAwards.has(seqItem.id)) {
            return { type: 'award', item: awardItem }
          }
        }
      }
      
      // If we've gone through the sequence, check from the beginning for any remaining items
      for (let i = 0; i < currentIndex; i++) {
        const seqItem = scoringSequence[i]
        
        if (seqItem.type === 'criteria') {
          const criteriaItem = unlockedCriteriaList.find(c => c.id === seqItem.id)
          if (criteriaItem && !newCompletedCriteria.has(seqItem.id)) {
            return { type: 'criteria', item: criteriaItem }
          }
        } else if (seqItem.type === 'award') {
          const awardItem = specialAwards.find(a => a.id === seqItem.id)
          if (awardItem && !newCompletedAwards.has(seqItem.id)) {
            return { type: 'award', item: awardItem }
          }
        }
      }
      
      return null // All items in sequence are completed
    }
    
    // Fallback: If no unified sequence, use display_order (criteria first, then awards)
    const pendingCriteria = unlockedCriteriaList.filter(c => !newCompletedCriteria.has(c.id))
    const pendingAwards = specialAwards.filter(a => !newCompletedAwards.has(a.id))
    
    if (justCompletedType === 'criteria') {
      if (pendingCriteria.length > 0) {
        return { type: 'criteria', item: pendingCriteria[0] }
      }
      if (pendingAwards.length > 0) {
        return { type: 'award', item: pendingAwards[0] }
      }
    }
    
    if (justCompletedType === 'award') {
      if (pendingAwards.length > 0) {
        return { type: 'award', item: pendingAwards[0] }
      }
      if (pendingCriteria.length > 0) {
        return { type: 'criteria', item: pendingCriteria[0] }
      }
    }
    
    return null
  }

  // Handle clicking on the recommendation to navigate to that item
  const handleRecommendationClick = () => {
    if (!recommendedItem) return
    
    if (recommendedItem.type === 'criteria') {
      setExpandedCriteria(recommendedItem.item.id)
      setExpandedAwards(null)
    } else if (recommendedItem.type === 'award') {
      setExpandedAwards(recommendedItem.item.id)
      setExpandedCriteria(null)
    }
    
    setShowRecommendation(false)
    setRecommendedItem(null)
    
    // Scroll to the item smoothly
    setTimeout(() => {
      const element = document.getElementById(`${recommendedItem.type}-${recommendedItem.item.id}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  // Dismiss the recommendation
  const dismissRecommendation = () => {
    setShowRecommendation(false)
    setRecommendedItem(null)
  }

  // Load event data on component mount
  useEffect(() => {
    if (event) {
      loadEventData()
    } else {
      fetchEventData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const loadEventData = async () => {
    try {
      setLoading(true)
      
      // Load criteria
      const { data: criteriaData, error: criteriaError } = await supabase
        .from(TABLES.CRITERIA)
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true })

      if (criteriaError) throw criteriaError

      // Load candidates
      const { data: candidatesData, error: candidatesError } = await supabase
        .from(TABLES.CANDIDATES)
        .select('*')
        .eq('event_id', eventId)
        .order('candidate_number')

      if (candidatesError) throw candidatesError

      // Load special awards
      const { data: awardsData, error: awardsError } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .select('*')
        .eq('event_id', eventId)
        .eq('award_type', 'vote')
        .order('display_order', { nullsFirst: false })
        .order('created_at')

      if (awardsError) {
        console.warn('No special awards found:', awardsError)
      }

      // Load existing scores
      await loadExistingScores()

      // Show all criteria (including locked ones) so judges can see them
      setCriteria(criteriaData || [])
      setCandidates(candidatesData || [])
      setSpecialAwards(awardsData || [])
      setEventData(event)
      
      // Load scoring sequence from event if available
      if (event.scoring_sequence && Array.isArray(event.scoring_sequence)) {
        setScoringSequence(event.scoring_sequence)
      }
      
      // Load judge certification status and change requests
      const { data: judgeData, error: judgeError } = await supabase
        .from(TABLES.JUDGES)
        .select('scores_certified, certification_timestamp')
        .eq('id', judgeId)
        .single()
      
      if (judgeData) {
        setIsCertified(judgeData.scores_certified || false)
        setCertificationTimestamp(judgeData.certification_timestamp)
      }
      
      // Load existing change requests
      await loadChangeRequests()
    } catch (error) {
      console.error('Error loading event data:', error)
      toast.error('Failed to load event data')
      navigate('/access-event')
    } finally {
      setLoading(false)
    }
  }

  const fetchEventData = async () => {
    try {
      setLoading(true)
      
      // Fetch event details
      const { data: eventData, error: eventError } = await supabase
        .from(TABLES.EVENTS)
        .select('*')
        .eq('id', eventId)
        .single()

      if (eventError) throw eventError

      // Load criteria
      const { data: criteriaData, error: criteriaError } = await supabase
        .from(TABLES.CRITERIA)
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true })

      if (criteriaError) throw criteriaError

      // Load candidates
      const { data: candidatesData, error: candidatesError } = await supabase
        .from(TABLES.CANDIDATES)
        .select('*')
        .eq('event_id', eventId)
        .order('candidate_number')

      if (candidatesError) throw candidatesError

      // Load special awards
      const { data: awardsData, error: awardsError } = await supabase
        .from(TABLES.SPECIAL_AWARDS)
        .select('*')
        .eq('event_id', eventId)
        .eq('award_type', 'vote')
        .order('display_order', { nullsFirst: false })
        .order('created_at')

      if (awardsError) {
        console.warn('No special awards found:', awardsError)
      }

      // Load existing scores
      await loadExistingScores()

      // Show all criteria (including locked ones) so judges can see them
      setCriteria(criteriaData || [])
      setCandidates(candidatesData || [])
      setSpecialAwards(awardsData || [])
      setEventData(eventData)
      
      // Load scoring sequence from event if available
      if (eventData.scoring_sequence && Array.isArray(eventData.scoring_sequence)) {
        setScoringSequence(eventData.scoring_sequence)
      }
      
    } catch (error) {
      console.error('Error fetching event data:', error)
      toast.error('Failed to load event data')
      navigate('/access-event')
    } finally {
      setLoading(false)
    }
  }

  const loadChangeRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('score_change_requests')
        .select('*')
        .eq('judge_id', judgeId)
        .eq('event_id', eventId)
      
      if (error) {
        console.warn('Could not load change requests:', error)
        return
      }
      
      setChangeRequests(data || [])
    } catch (error) {
      console.error('Error loading change requests:', error)
    }
  }

  const loadExistingScores = async () => {
    try {
      // Load judge's completion status from the judges table
      const { data: judgeData, error: judgeError } = await supabase
        .from(TABLES.JUDGES)
        .select('completed_criteria, submitted_special_awards, submitted_candidates')
        .eq('id', judgeId)
        .single()

      if (judgeError) {
        console.warn('Could not load judge completion status:', judgeError)
      }

      // Load criteria scores
      const { data: criteriaScoresData, error: criteriaScoresError } = await supabase
        .from(TABLES.SCORES)
        .select('*')
        .eq('event_id', eventId)
        .eq('judge_id', judgeId)

      if (criteriaScoresError) throw criteriaScoresError

      // Load award scores
      const { data: awardScoresData, error: awardScoresError } = await supabase
        .from(TABLES.SPECIAL_AWARD_VOTES)
        .select('*')
        .eq('judge_id', judgeId)

      if (awardScoresError) {
        console.warn('No award scores found:', awardScoresError)
      }

      // Process criteria scores
      const criteriaScoresMap = {}
      
      if (criteriaScoresData) {
        criteriaScoresData.forEach(score => {
          const key = `${score.candidate_id}-${score.criteria_id}`
          criteriaScoresMap[key] = score.score
        })
      }

      // Process award scores
      const awardScoresMap = {}
      
      if (awardScoresData) {
        awardScoresData.forEach(score => {
          const key = `${score.candidate_id}-${score.special_award_id}`
          awardScoresMap[key] = score.score
        })
      }

      // Load completion status from database
      const completedCriteriaSet = new Set(judgeData?.completed_criteria || [])
      const completedAwardsSet = new Set(judgeData?.submitted_special_awards || [])
      const submittedCandidatesSet = new Set(judgeData?.submitted_candidates || [])

      console.log('Loaded judge completion status:', {
        completedCriteria: Array.from(completedCriteriaSet),
        completedAwards: Array.from(completedAwardsSet),
        submittedCandidates: Array.from(submittedCandidatesSet),
        judgeData
      })

      setCriteriaScores(criteriaScoresMap)
      setAwardScores(awardScoresMap)
      setCompletedCriteria(completedCriteriaSet)
      setCompletedAwards(completedAwardsSet)
      setSubmittedCandidates(submittedCandidatesSet)

    } catch (error) {
      console.error('Error loading existing scores:', error)
    }
  }

  // Criteria scoring functions
  const handleCriteriaScoreChange = (candidateId, criteriaId, score) => {
    const criterion = criteria.find(c => c.id === criteriaId)
    const maxScore = criterion ? criterion.percentage : 100
    const normalizedScore = Math.min(Math.max(parseInt(score) || 0, 0), maxScore)
    
    const key = `${candidateId}-${criteriaId}`
    setCriteriaScores(prev => ({
      ...prev,
      [key]: normalizedScore
    }))
  }

  const isCriteriaComplete = (criteriaId) => {
    return completedCriteria.has(criteriaId)
  }

  const canSubmitCriteria = (criteriaId) => {
    return candidates.every(candidate => {
      const key = `${candidate.id}-${criteriaId}`
      const hasScore = criteriaScores[key] !== undefined && criteriaScores[key] !== null
      const isLocked = lockedScores[key] === true
      return hasScore && isLocked
    })
  }

  const handleSubmitCriteriaClick = (criteriaId) => {
    if (!canSubmitCriteria(criteriaId)) {
      toast.error('Please lock all scores before submitting')
      return
    }
    
    setPendingSubmission({ type: 'criteria', id: criteriaId })
    setShowConfirmationModal(true)
  }

  const submitCriteriaScores = async (criteriaId) => {
    setSubmitting(true)
    setShowConfirmationModal(false)
    
    try {
      const scoreData = candidates.map(candidate => ({
        event_id: eventId,
        judge_id: judgeId,
        candidate_id: candidate.id,
        criteria_id: criteriaId,
        score: criteriaScores[`${candidate.id}-${criteriaId}`] || 0
      }))

      const { error } = await supabase
        .from(TABLES.SCORES)
        .insert(scoreData)

      if (error) throw error

      // Update judge's completed criteria in the database
      const newCompletedCriteria = new Set([...completedCriteria, criteriaId])
      const { error: updateError } = await supabase
        .from(TABLES.JUDGES)
        .update({ completed_criteria: Array.from(newCompletedCriteria) })
        .eq('id', judgeId)

      if (updateError) {
        console.error('Error updating judge completion status:', updateError)
        throw new Error('Failed to update completion status')
      }

      setCompletedCriteria(newCompletedCriteria)
      toast.success('Criteria scores submitted successfully!')
      
      // Collapse the criteria card after successful submission
      setExpandedCriteria(null)
      
      // Get and show next recommended item
      const nextItem = getNextRecommendedItem('criteria', criteriaId, newCompletedCriteria, completedAwards)
      if (nextItem) {
        setRecommendedItem(nextItem)
        setShowRecommendation(true)
      }
      
    } catch (error) {
      console.error('Error submitting criteria scores:', error)
      toast.error('Failed to submit scores')
    } finally {
      setSubmitting(false)
      setPendingSubmission(null)
    }
  }

  // Award scoring functions
  const handleAwardScoreChange = (candidateId, awardId, score) => {
    const normalizedScore = Math.min(Math.max(parseInt(score) || 0, 0), 100)
    
    const key = `${candidateId}-${awardId}`
    setAwardScores(prev => ({
      ...prev,
      [key]: normalizedScore
    }))
  }

  const isAwardComplete = (awardId) => {
    return completedAwards.has(awardId)
  }

  const canSubmitAward = (awardId) => {
    return candidates.every(candidate => {
      const key = `${candidate.id}-${awardId}`
      const hasScore = awardScores[key] !== undefined && awardScores[key] !== null
      const isLocked = lockedScores[key] === true
      return hasScore && isLocked
    })
  }

  const handleSubmitAwardClick = (awardId) => {
    if (!canSubmitAward(awardId)) {
      toast.error('Please lock all scores before submitting')
      return
    }
    
    setPendingSubmission({ type: 'award', id: awardId })
    setShowConfirmationModal(true)
  }

  const submitAwardScores = async (awardId) => {
    setSubmitting(true)
    setShowConfirmationModal(false)
    
    try {
      // Delete existing scores for this award
      await supabase
        .from(TABLES.SPECIAL_AWARD_VOTES)
        .delete()
        .eq('judge_id', judgeId)
        .eq('special_award_id', awardId)

      const scoreData = candidates.map(candidate => ({
        special_award_id: awardId,
        judge_id: judgeId,
        candidate_id: candidate.id,
        score: awardScores[`${candidate.id}-${awardId}`] || 0
      }))

      const { error } = await supabase
        .from(TABLES.SPECIAL_AWARD_VOTES)
        .insert(scoreData)

      if (error) throw error

      // Update judge's submitted special awards in the database
      const newCompletedAwards = new Set([...completedAwards, awardId])
      const { error: updateError } = await supabase
        .from(TABLES.JUDGES)
        .update({ submitted_special_awards: Array.from(newCompletedAwards) })
        .eq('id', judgeId)

      if (updateError) {
        console.error('Error updating judge special awards status:', updateError)
        throw new Error('Failed to update completion status')
      }

      setCompletedAwards(newCompletedAwards)
      toast.success('Award scores submitted successfully!')
      
      // Collapse the award card after successful submission
      setExpandedAwards(null)
      
      // Get and show next recommended item
      const nextItem = getNextRecommendedItem('award', awardId, completedCriteria, newCompletedAwards)
      if (nextItem) {
        setRecommendedItem(nextItem)
        setShowRecommendation(true)
      }
      
    } catch (error) {
      console.error('Error submitting award scores:', error)
      toast.error('Failed to submit scores')
    } finally {
      setSubmitting(false)
      setPendingSubmission(null)
    }
  }

  const canSubmitCandidate = (candidateId) => {
    // Check criteria scores
    const unlockedCriteria = criteria.filter(c => !c.is_locked)
    for (const criterion of unlockedCriteria) {
      const scoreKey = `${candidateId}-${criterion.id}`
      if (!isCriteriaComplete(criterion.id) && lockedScores[scoreKey] !== true) {
        return false
      }
    }
    
    // Check special awards
    for (const award of specialAwards) {
      const scoreKey = `${candidateId}-${award.id}`
      if (!isAwardComplete(award.id) && lockedScores[scoreKey] !== true) {
        return false
      }
    }
    
    return true
  }

  const handleSubmitCandidateClick = (candidateId) => {
    if (!canSubmitCandidate(candidateId)) {
      toast.error('Please lock all scores for this candidate before submitting')
      return
    }
    
    setPendingSubmission({ type: 'candidate', id: candidateId })
    setShowConfirmationModal(true)
  }

  const submitCandidateScores = async (candidateId) => {
    setSubmitting(true)
    setShowConfirmationModal(false)
    
    try {
      // 1. Prepare criteria scores for this candidate
      const candidateCriteriaScores = criteria.filter(c => !c.is_locked).map(criterion => ({
        event_id: eventId,
        judge_id: judgeId,
        candidate_id: candidateId,
        criteria_id: criterion.id,
        score: criteriaScores[`${candidateId}-${criterion.id}`] || 0
      }))

      // Delete existing scores for this candidate/criteria first
      for (const criterion of criteria) {
        await supabase
          .from(TABLES.SCORES)
          .delete()
          .eq('judge_id', judgeId)
          .eq('candidate_id', candidateId)
          .eq('criteria_id', criterion.id)
      }

      // Insert new criteria scores
      if (candidateCriteriaScores.length > 0) {
        const { error: criteriaError } = await supabase
          .from(TABLES.SCORES)
          .insert(candidateCriteriaScores)
        if (criteriaError) throw criteriaError
      }

      // 2. Prepare special award scores for this candidate
      const candidateAwardScores = specialAwards.map(award => ({
        judge_id: judgeId,
        candidate_id: candidateId,
        special_award_id: award.id,
        score: awardScores[`${candidateId}-${award.id}`] || 0
      }))

      // Delete existing award votes for this candidate first
      for (const award of specialAwards) {
        await supabase
          .from(TABLES.SPECIAL_AWARD_VOTES)
          .delete()
          .eq('judge_id', judgeId)
          .eq('candidate_id', candidateId)
          .eq('special_award_id', award.id)
      }

      // Insert new award votes
      if (candidateAwardScores.length > 0) {
        const { error: awardError } = await supabase
          .from(TABLES.SPECIAL_AWARD_VOTES)
          .insert(candidateAwardScores)
        if (awardError) throw awardError
      }

      // 3. Update submitted candidates in judge document
      const newSubmittedCandidates = new Set([...submittedCandidates, candidateId])
      setSubmittedCandidates(newSubmittedCandidates)

      const updateData = {
        submitted_candidates: Array.from(newSubmittedCandidates)
      }

      // If all candidates are now submitted, mark all criteria and awards as completed
      const allCandidatesSubmitted = newSubmittedCandidates.size === candidates.length
      if (allCandidatesSubmitted) {
        const allCriteriaIds = criteria.filter(c => !c.is_locked).map(c => c.id)
        const allAwardIds = specialAwards.map(a => a.id)
        
        const newCompletedCriteria = new Set([...completedCriteria, ...allCriteriaIds])
        const newCompletedAwards = new Set([...completedAwards, ...allAwardIds])
        
        setCompletedCriteria(newCompletedCriteria)
        setCompletedAwards(newCompletedAwards)
        
        updateData.completed_criteria = Array.from(newCompletedCriteria)
        updateData.submitted_special_awards = Array.from(newCompletedAwards)
      }

      const { error: updateError } = await supabase
        .from(TABLES.JUDGES)
        .update(updateData)
        .eq('id', judgeId)

      if (updateError) throw updateError

      toast.success('Candidate scores submitted successfully!')
      setExpandedCandidate(null)
    } catch (error) {
      console.error('Error submitting candidate scores:', error)
      toast.error('Failed to submit scores')
    } finally {
      setSubmitting(false)
      setPendingSubmission(null)
    }
  }

  // UI helper functions - single expansion behavior
  const toggleCriteriaExpansion = (criteriaId) => {
    setExpandedCriteria(prev => prev === criteriaId ? null : criteriaId)
    // Close awards when opening criteria
    if (expandedAwards) {
      setExpandedAwards(null)
    }
  }

  const toggleAwardExpansion = (awardId) => {
    setExpandedAwards(prev => prev === awardId ? null : awardId)
    // Close criteria when opening awards
    if (expandedCriteria) {
      setExpandedCriteria(null)
    }
  }

  const handleConfirmSubmission = () => {
    if (pendingSubmission?.type === 'criteria') {
      submitCriteriaScores(pendingSubmission.id)
    } else if (pendingSubmission?.type === 'award') {
      submitAwardScores(pendingSubmission.id)
    } else if (pendingSubmission?.type === 'candidate') {
      submitCandidateScores(pendingSubmission.id)
    }
  }

  const handleCancelSubmission = () => {
    setShowConfirmationModal(false)
    setPendingSubmission(null)
  }

  const handleRequestChange = (type, id, name) => {
    setChangeRequestData({ type, id, name })
    setChangeRequestReason('')
    setShowChangeRequestModal(true)
  }
  
  const submitChangeRequest = async () => {
    if (!changeRequestReason.trim()) {
      toast.error('Please provide a reason for the change request')
      return
    }
    
    try {
      setRequestingChange(true)
      
      const requestPayload = {
        event_id: eventId,
        judge_id: judgeId,
        request_type: changeRequestData.type,
        reason: changeRequestReason.trim(),
        status: 'pending'
      }
      
      if (changeRequestData.type === 'criteria') {
        requestPayload.criteria_id = changeRequestData.id
      } else {
        requestPayload.special_award_id = changeRequestData.id
      }
      
      const { error } = await supabase
        .from('score_change_requests')
        .insert([requestPayload])
      
      if (error) throw error
      
      toast.success('Change request submitted! Waiting for admin approval.')
      setShowChangeRequestModal(false)
      setChangeRequestData(null)
      setChangeRequestReason('')
      
      // Reload change requests
      await loadChangeRequests()
      
    } catch (error) {
      console.error('Error submitting change request:', error)
      toast.error('Failed to submit change request')
    } finally {
      setRequestingChange(false)
    }
  }
  
  const hasActiveRequest = (type, id) => {
    return changeRequests.some(req => 
      req.request_type === type && 
      (type === 'criteria' ? req.criteria_id === id : req.special_award_id === id) &&
      req.status === 'pending'
    )
  }
  
  const hasApprovedRequest = (type, id) => {
    return changeRequests.some(req => 
      req.request_type === type && 
      (type === 'criteria' ? req.criteria_id === id : req.special_award_id === id) &&
      req.status === 'approved'
    )
  }
  
  const submitHelpRequest = async () => {
    if (!helpMessage.trim()) {
      toast.error('Please describe what help you need')
      return
    }
    
    try {
      setSendingHelp(true)
      
      const { error } = await supabase
        .from('help_requests')
        .insert([{
          event_id: eventId,
          judge_id: judgeId,
          message: helpMessage.trim(),
          status: 'pending'
        }])
      
      if (error) throw error
      
      toast.success('Help request sent! An admin will assist you shortly.')
      setShowHelpModal(false)
      setHelpMessage('')
      
    } catch (error) {
      console.error('Error submitting help request:', error)
      toast.error('Failed to send help request')
    } finally {
      setSendingHelp(false)
    }
  }
  
  const handleReopenForEdit = async (type, id) => {
    try {
      // Remove from completed status to allow editing
      if (type === 'criteria') {
        const newCompletedCriteria = new Set(completedCriteria)
        newCompletedCriteria.delete(id)
        setCompletedCriteria(newCompletedCriteria)
        
        // Update in database
        await supabase
          .from(TABLES.JUDGES)
          .update({ completed_criteria: Array.from(newCompletedCriteria) })
          .eq('id', judgeId)
        
        // Expand the criteria
        setExpandedCriteria(id)
        setExpandedAwards(null)
      } else {
        const newCompletedAwards = new Set(completedAwards)
        newCompletedAwards.delete(id)
        setCompletedAwards(newCompletedAwards)
        
        // Update in database
        await supabase
          .from(TABLES.JUDGES)
          .update({ submitted_special_awards: Array.from(newCompletedAwards) })
          .eq('id', judgeId)
        
        // Expand the award
        setExpandedAwards(id)
        setExpandedCriteria(null)
      }
      
      // Delete the existing scores to allow fresh entry
      if (type === 'criteria') {
        await supabase
          .from(TABLES.SCORES)
          .delete()
          .eq('judge_id', judgeId)
          .eq('criteria_id', id)
      } else {
        await supabase
          .from(TABLES.SPECIAL_AWARD_VOTES)
          .delete()
          .eq('judge_id', judgeId)
          .eq('special_award_id', id)
      }
      
      // Mark the request as used by updating it
      await supabase
        .from('score_change_requests')
        .update({ status: 'used' })
        .eq('judge_id', judgeId)
        .eq('request_type', type)
        .eq(type === 'criteria' ? 'criteria_id' : 'special_award_id', id)
        .eq('status', 'approved')
      
      // Reload change requests
      await loadChangeRequests()
      
      toast.success('You can now edit your scores. Please re-submit when done.')
    } catch (error) {
      console.error('Error reopening for edit:', error)
      toast.error('Failed to reopen for editing')
    }
  }

  const handleCertifyScores = async () => {
    if (!window.confirm('Are you sure you want to certify all your submitted scores as correct? This action confirms that you have reviewed and approved all your scoring.')) {
      return
    }

    try {
      setCertifying(true)
      
      const { error } = await supabase
        .from(TABLES.JUDGES)
        .update({
          scores_certified: true,
          certification_timestamp: new Date().toISOString()
        })
        .eq('id', judgeId)

      if (error) throw error

      setIsCertified(true)
      setCertificationTimestamp(new Date().toISOString())
      toast.success('Your scores have been certified successfully!')
      
    } catch (error) {
      console.error('Error certifying scores:', error)
      toast.error('Failed to certify scores. Please try again.')
    } finally {
      setCertifying(false)
    }
  }

  // Calculate overall progress (only count unlocked criteria)
  const unlockedCriteria = criteria.filter(c => !c.is_locked)
  const totalTasks = unlockedCriteria.length + specialAwards.length
  const completedTasks = completedCriteria.size + completedAwards.size
  const progressPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  if (!eventData || !judgeId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-3 text-sm">Access denied</p>
          <button 
            onClick={() => {
              localStorage.removeItem(`judgeId_${eventId}`)
              localStorage.removeItem(`judgeName_${eventId}`)
              localStorage.removeItem(`event_${eventId}`)
              navigate('/access-event')
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm"
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Compact Sticky Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-3 py-2 sm:px-4 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Left: Logo + Event Name */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <div className="hidden sm:block">
                {eventData.logo_url ? (
                  <img 
                    src={eventData.logo_url} 
                    alt="" 
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex items-center gap-2">
                <div>
                  <h1 className="text-xs sm:text-base font-bold text-gray-900 truncate max-w-[120px] sm:max-w-none">{eventData.name}</h1>
                  <p className="text-[10px] sm:text-xs text-gray-500 truncate">Judge: {judgeName}</p>
                </div>
                {/* Compact Progress Badge for mobile */}
                <span className="sm:hidden inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-800">
                  {completedTasks}/{totalTasks}
                </span>
              </div>
            </div>
            
            {/* Right: Help & Exit Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHelpModal(true)}
                className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-100 rounded-lg flex-shrink-0"
                title="Need Help?"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem(`judgeId_${eventId}`)
                  localStorage.removeItem(`judgeName_${eventId}`)
                  localStorage.removeItem(`event_${eventId}`)
                  navigate('/access-event')
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg flex-shrink-0"
                title="Exit"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="hidden sm:block mt-2">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>{completedTasks}/{totalTasks} completed</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          
          {/* View Toggles (Scoring Mode & Column count) */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2 sm:mt-3 sm:pt-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 hidden sm:inline">Scoring Mode:</span>
              <div className="flex bg-gray-100 p-0.5 rounded-lg">
                <button
                  onClick={() => setScoringMode('criteria')}
                  className={`px-2.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    scoringMode === 'criteria' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  By Criteria/Awards
                </button>
                <button
                  onClick={() => setScoringMode('candidate')}
                  className={`px-2.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    scoringMode === 'candidate' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  By Candidate
                </button>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-2">
              <span className="text-xs text-gray-500">Columns:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => changeColumnView(1)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    columnView === 1 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Single column"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => changeColumnView(2)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    columnView === 2 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Two columns"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v14a2 2 0 002 2h4m0-18v18m0-18h6m-6 0v18m6-18h4a2 2 0 012 2v14a2 2 0 01-2 2h-4m-6 0h6" />
                  </svg>
                </button>
                <button
                  onClick={() => changeColumnView(3)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                    columnView === 3 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Three columns"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v14a2 2 0 002 2h4m0-18v18m0-18h10m-10 0v18m0-18h10m-10 18h10m-10 0H5m14 0v-2" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Sleek Minimalist Progress Bar for mobile, running along the bottom edge of the header */}
        <div className="sm:hidden w-full h-0.5 bg-gray-100 overflow-hidden">
          <div 
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="p-3 sm:p-4 pb-20">
        {/* Completion Banner - Show when all tasks complete */}
        {totalTasks > 0 && completedTasks === totalTasks && (
          <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-green-900 mb-1">All Submissions Complete! 🎉</h3>
                <p className="text-sm text-green-800 mb-3">
                  You've successfully submitted all criteria and special award scores.
                </p>
                <button
                  onClick={() => setShowDetailedReview(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors shadow"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  View All My Scores
                </button>
              </div>
            </div>
          </div>
        )}
        
        {scoringMode === 'criteria' ? (
          <>
            {/* Criteria Section */}
            <div className="mb-4">
          <div className="flex items-center gap-2 mb-3 px-1">
            <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900">Criteria</h2>
            <span className="text-xs text-gray-500 ml-auto">{completedCriteria.size}/{unlockedCriteria.length}</span>
          </div>
          
          <div className="space-y-2">
            {criteria.map((criterion) => {
              const isExpanded = expandedCriteria === criterion.id
              const isCompleted = isCriteriaComplete(criterion.id)
              const isLocked = criterion.is_locked
              
              // Locked criteria - display only (non-interactive)
              if (isLocked) {
                return (
                  <div key={criterion.id} className="bg-purple-50 rounded-xl shadow-sm border border-purple-300">
                    <div className="p-3 sm:p-4">
                      <div className="flex items-center gap-3">
                        {/* Lock Icon */}
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-purple-100">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        
                        {/* Name & Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-purple-900 truncate">{criterion.name}</h3>
                          <p className="text-xs text-purple-600">Max: {criterion.percentage} pts • Admin Managed</p>
                        </div>
                        
                        {/* Locked Badge */}
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-200 text-purple-800 flex-shrink-0">
                          Locked
                        </span>
                      </div>
                      
                      {/* Admin Notes */}
                      {criterion.admin_notes && (
                        <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-xs text-purple-800">
                              <p className="font-medium mb-1">Admin Note:</p>
                              <p className="whitespace-pre-wrap leading-relaxed">{criterion.admin_notes}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Description if present */}
                      {criterion.description && !criterion.admin_notes && (
                        <div className="mt-3 text-xs text-purple-700">
                          {criterion.description}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }
              
              // Regular unlocked criteria - interactive
              return (
                <div 
                  key={criterion.id} 
                  id={`criteria-${criterion.id}`}
                  className={`bg-white rounded-xl shadow-sm border transition-all ${
                    isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200'
                  }`}
                >
                  {/* Criteria Header - Compact */}
                  <div className="p-3 sm:p-4">
                    <div 
                      className="cursor-pointer active:bg-gray-50 rounded-lg"
                      onClick={() => toggleCriteriaExpansion(criterion.id)}
                    >
                      <div className="flex items-center gap-3">
                        {/* Status Icon */}
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isCompleted ? 'bg-green-100' : 'bg-blue-50'
                        }`}>
                          {isCompleted ? (
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-blue-600 font-bold text-sm">{criterion.percentage}</span>
                          )}
                        </div>
                        
                        {/* Name & Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{criterion.name}</h3>
                          <p className="text-xs text-gray-500">Max: {criterion.percentage} pts</p>
                        </div>
                        
                        {/* Status Badge, Request Change Icon & Arrow */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isCompleted 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isCompleted ? '✓' : 'Pending'}
                          </span>
                          
                          {/* Request Change Icon - Only show when completed */}
                          {isCompleted && (
                            <>
                              {hasApprovedRequest('criteria', criterion.id) ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleReopenForEdit('criteria', criterion.id)
                                  }}
                                  className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors"
                                  title="Edit Scores (Approved)"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                              ) : hasActiveRequest('criteria', criterion.id) ? (
                                <div className="p-1.5 rounded-lg bg-amber-100" title="Change request pending admin approval">
                                  <svg className="w-4 h-4 text-amber-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRequestChange('criteria', criterion.id, criterion.name)
                                  }}
                                  className="p-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                                  title="Request Change"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </>
                          )}
                          
                          <svg 
                            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Scoring Panel */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 rounded-b-xl">
                      {/* Criteria Description - Minimal Guide */}
                      {criterion.description && (
                        <div className="px-3 pt-3 sm:px-4 sm:pt-4">
                          <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
                            <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-xs text-blue-700 leading-relaxed">{criterion.description}</p>
                          </div>
                        </div>
                      )}
                      {/* Candidates List */}
                      <div className={`p-3 sm:p-4 grid gap-2 ${
                        columnView === 1 ? 'grid-cols-1' :
                        columnView === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                        'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                      }`}>
                        {candidates.map((candidate) => {
                          const scoreKey = `${candidate.id}-${criterion.id}`
                          const currentScore = criteriaScores[scoreKey] || 0
                          const isLocked = isScoreLocked(scoreKey)
                          
                          return (
                            <div key={candidate.id} className={`bg-white rounded-lg p-3 border transition-colors ${
                              isLocked ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                            }`}>
                              <div className="flex items-center gap-2 sm:gap-3">
                                {/* Candidate Number */}
                                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                                  isLocked ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                                }`}>
                                  {candidate.candidate_number}
                                </div>
                                
                                {/* Candidate Info */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{candidate.name}</p>
                                  {candidate.representation && (
                                    <p className="text-xs text-gray-500 truncate">{candidate.representation}</p>
                                  )}
                                </div>
                                
                                {/* Score Input + Lock */}
                                {isCompleted ? (
                                  <div className="text-right flex-shrink-0">
                                    <span className="text-lg font-bold text-green-600">{currentScore}</span>
                                    <span className="text-xs text-gray-400">/{criterion.percentage}</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                    <input
                                      type="number"
                                      min="0"
                                      max={criterion.percentage}
                                      value={currentScore}
                                      onChange={(e) => handleCriteriaScoreChange(candidate.id, criterion.id, e.target.value)}
                                      disabled={isLocked}
                                      className={`w-14 sm:w-16 px-2 py-2 border rounded-lg text-center text-sm font-semibold transition-colors ${
                                        isLocked 
                                          ? 'bg-green-100 border-green-300 text-green-700 cursor-not-allowed' 
                                          : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                                      }`}
                                    />
                                    {/* Lock Button */}
                                    <button
                                      onClick={() => toggleScoreLock(scoreKey)}
                                      className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                                        isLocked 
                                          ? 'bg-green-500 text-white active:bg-green-600' 
                                          : 'bg-gray-100 text-gray-500 active:bg-gray-200'
                                      }`}
                                      title={isLocked ? 'Unlock score' : 'Lock score'}
                                    >
                                      {isLocked ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                      ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              {/* Slider - Only when not completed and not locked */}
                              {!isCompleted && !isLocked && (
                                <div className="mt-2">
                                  <input
                                    type="range"
                                    min="0"
                                    max={criterion.percentage}
                                    value={currentScore}
                                    onChange={(e) => handleCriteriaScoreChange(candidate.id, criterion.id, e.target.value)}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    style={{
                                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentScore / criterion.percentage) * 100}%, #e5e7eb ${(currentScore / criterion.percentage) * 100}%, #e5e7eb 100%)`
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Locked indicator */}
                              {!isCompleted && isLocked && (
                                <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Score locked</span>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>

                      {/* Submit Button */}
                      {!isCompleted && (
                        <div className="p-3 sm:p-4 pt-0 space-y-2">
                          {!canSubmitCriteria(criterion.id) && (
                            <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                              <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              <p className="text-xs text-amber-800 leading-relaxed">
                                Lock all scores before submitting. Click the lock icon next to each score to secure it.
                              </p>
                            </div>
                          )}
                          <button
                            onClick={() => handleSubmitCriteriaClick(criterion.id)}
                            disabled={submitting || !canSubmitCriteria(criterion.id)}
                            className="w-full py-3 bg-blue-500 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:bg-blue-600"
                          >
                            {submitting ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Submit Scores
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Special Awards Section */}
        {specialAwards.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="w-6 h-6 bg-purple-500 rounded-md flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900">Special Awards</h2>
              <span className="text-xs text-gray-500 ml-auto">{completedAwards.size}/{specialAwards.length}</span>
            </div>
            
            <div className="space-y-2">
              {specialAwards.map((award) => {
                const isExpanded = expandedAwards === award.id
                const isCompleted = isAwardComplete(award.id)
                
                return (
                  <div 
                    key={award.id} 
                    id={`award-${award.id}`}
                    className={`bg-white rounded-xl shadow-sm border transition-all ${
                      isExpanded ? 'border-purple-300 shadow-md' : 'border-gray-200'
                    }`}
                  >
                    {/* Award Header - Compact */}
                    <div className="p-3 sm:p-4">
                      <div 
                        className="cursor-pointer active:bg-gray-50 rounded-lg"
                        onClick={() => toggleAwardExpansion(award.id)}
                      >
                        <div className="flex items-center gap-3">
                          {/* Status Icon */}
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isCompleted ? 'bg-green-100' : 'bg-purple-50'
                          }`}>
                            {isCompleted ? (
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Name & Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{award.name}</h3>
                            <p className="text-xs text-gray-500">Max: 100 pts</p>
                          </div>
                          
                          {/* Status Badge, Request Change Icon & Arrow */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isCompleted 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {isCompleted ? '✓' : 'Pending'}
                            </span>
                            
                            {/* Request Change Icon - Only show when completed */}
                            {isCompleted && (
                              <>
                                {hasApprovedRequest('award', award.id) ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleReopenForEdit('award', award.id)
                                    }}
                                    className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors"
                                    title="Edit Scores (Approved)"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                ) : hasActiveRequest('award', award.id) ? (
                                  <div className="p-1.5 rounded-lg bg-amber-100" title="Change request pending admin approval">
                                    <svg className="w-4 h-4 text-amber-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRequestChange('award', award.id, award.name)
                                    }}
                                    className="p-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors"
                                    title="Request Change"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                            
                            <svg 
                              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Scoring Panel */}
                    {isExpanded && (
                        <div className="border-t border-gray-100 bg-gray-50 rounded-b-xl">
                          {/* Award Description - Minimal Guide */}
                          {award.description && (
                            <div className="px-3 pt-3 sm:px-4 sm:pt-4">
                              <div className="flex items-start gap-2 p-2 bg-purple-50 border border-purple-100 rounded-lg">
                                <svg className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs text-purple-700 leading-relaxed">{award.description}</p>
                              </div>
                            </div>
                          )}
                        {/* Candidates List */}
                        <div className={`p-3 sm:p-4 grid gap-2 ${
                          columnView === 1 ? 'grid-cols-1' :
                          columnView === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                          'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                        }`}>
                          {candidates.map((candidate) => {
                            const scoreKey = `${candidate.id}-${award.id}`
                            const currentScore = awardScores[scoreKey] || 0
                            const isLocked = isScoreLocked(scoreKey)
                            
                            return (
                              <div key={candidate.id} className={`bg-white rounded-lg p-3 border transition-colors ${
                                isLocked ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
                              }`}>
                                <div className="flex items-center gap-2 sm:gap-3">
                                  {/* Candidate Number */}
                                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                                    isLocked ? 'bg-green-500 text-white' : 'bg-purple-500 text-white'
                                  }`}>
                                    {candidate.candidate_number}
                                  </div>
                                  
                                  {/* Candidate Info */}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{candidate.name}</p>
                                    {candidate.representation && (
                                      <p className="text-xs text-gray-500 truncate">{candidate.representation}</p>
                                    )}
                                  </div>
                                  
                                  {/* Score Input + Lock */}
                                  {isCompleted ? (
                                    <div className="text-right flex-shrink-0">
                                      <span className="text-lg font-bold text-green-600">{currentScore}</span>
                                      <span className="text-xs text-gray-400">/100</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentScore}
                                        onChange={(e) => handleAwardScoreChange(candidate.id, award.id, e.target.value)}
                                        disabled={isLocked}
                                        className={`w-14 sm:w-16 px-2 py-2 border rounded-lg text-center text-sm font-semibold transition-colors ${
                                          isLocked 
                                            ? 'bg-green-100 border-green-300 text-green-700 cursor-not-allowed' 
                                            : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                                        }`}
                                      />
                                      {/* Lock Button */}
                                      <button
                                        onClick={() => toggleScoreLock(scoreKey)}
                                        className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                                          isLocked 
                                            ? 'bg-green-500 text-white active:bg-green-600' 
                                            : 'bg-gray-100 text-gray-500 active:bg-gray-200'
                                        }`}
                                        title={isLocked ? 'Unlock score' : 'Lock score'}
                                      >
                                        {isLocked ? (
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                          </svg>
                                        ) : (
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Slider - Only when not completed and not locked */}
                                {!isCompleted && !isLocked && (
                                  <div className="mt-2">
                                    <input
                                      type="range"
                                      min="0"
                                      max="100"
                                      value={currentScore}
                                      onChange={(e) => handleAwardScoreChange(candidate.id, award.id, e.target.value)}
                                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                      style={{
                                        background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${currentScore}%, #e5e7eb ${currentScore}%, #e5e7eb 100%)`
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Locked indicator */}
                                {!isCompleted && isLocked && (
                                  <div className="mt-2 flex items-center gap-1 text-xs text-green-600">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Score locked</span>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>

                        {/* Submit Button */}
                        {!isCompleted && (
                          <div className="p-3 sm:p-4 pt-0 space-y-2">
                            {!canSubmitAward(award.id) && (
                              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <p className="text-xs text-amber-800 leading-relaxed">
                                  Lock all scores before submitting. Click the lock icon next to each score to secure it.
                                </p>
                              </div>
                            )}
                            <button
                              onClick={() => handleSubmitAwardClick(award.id)}
                              disabled={submitting || !canSubmitAward(award.id)}
                              className="w-full py-3 bg-purple-500 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:bg-purple-600"
                            >
                              {submitting ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Submit Votes
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
          </>
        ) : (
          <div className="space-y-6">
            {/* Candidates Section */}
            <div>
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h2 className="text-base sm:text-lg font-bold text-gray-900">Candidates</h2>
              </div>
              
              <div className={`grid gap-3 ${
                columnView === 1 ? 'grid-cols-1' :
                columnView === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
              }`}>
                {candidates.map(candidate => {
                  const isExpanded = expandedCandidate === candidate.id
                  const isSubmitted = isCandidateSubmitted(candidate.id)
                  const lockedCount = getCandidateLockedCount(candidate.id)
                  const totalTasks = getCandidateTotalTasks()
                  
                  return (
                    <div 
                      key={candidate.id}
                      className={`bg-white rounded-xl shadow-sm border transition-all ${
                        isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200'
                      }`}
                    >
                      {/* Candidate Header */}
                      <div className="p-3 sm:p-4">
                        <div 
                          className="cursor-pointer active:bg-gray-50 rounded-lg"
                          onClick={() => toggleCandidateExpansion(candidate.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 bg-blue-500 text-white">
                              {candidate.candidate_number}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{candidate.name}</h3>
                              {candidate.representation && (
                                <p className="text-xs text-gray-500 truncate">{candidate.representation}</p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                isSubmitted 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {isSubmitted ? 'Submitted' : `${lockedCount}/${totalTasks}`}
                              </span>
                              
                              <svg 
                                className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Candidate Score Input Area */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 bg-gray-50 rounded-b-xl p-3 sm:p-4 space-y-4">
                          {/* Criteria list for this candidate */}
                          {criteria.filter(c => !c.is_locked).length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Criteria</h4>
                              <div className="space-y-2">
                                {criteria.filter(c => !c.is_locked).map(criterion => {
                                  const scoreKey = `${candidate.id}-${criterion.id}`
                                  const currentScore = criteriaScores[scoreKey] || 0
                                  const isLocked = isScoreLocked(scoreKey)
                                  const isCompleted = isCriteriaComplete(criterion.id)
                                  
                                  const isDisabled = isCompleted || isLocked || (isSubmitted && !hasApprovedRequest('criteria', criterion.id))
                                  const showLockBtn = !isCompleted && !(isSubmitted && !hasApprovedRequest('criteria', criterion.id))
                                  
                                  return (
                                    <div key={criterion.id} className="p-3 bg-white rounded-lg border border-gray-200">
                                      <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-semibold text-gray-900 truncate">{criterion.name}</p>
                                          <p className="text-xs text-gray-500">Max: {criterion.percentage} pts</p>
                                        </div>
                                        {!showLockBtn ? (
                                          <div className="text-right flex-shrink-0">
                                            <span className="text-base font-bold text-green-600">{currentScore}</span>
                                            <span className="text-xs text-gray-400">/{criterion.percentage}</span>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <input
                                              type="number"
                                              min="0"
                                              max={criterion.percentage}
                                              value={currentScore}
                                              onChange={(e) => handleCriteriaScoreChange(candidate.id, criterion.id, e.target.value)}
                                              disabled={isDisabled}
                                              className={`w-14 px-2 py-1.5 border rounded-lg text-center text-sm font-semibold transition-colors ${
                                                isDisabled 
                                                  ? 'bg-green-100 border-green-300 text-green-700 cursor-not-allowed' 
                                                  : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                                              }`}
                                            />
                                            <button
                                              onClick={() => toggleScoreLock(scoreKey)}
                                              className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                                                isLocked 
                                                  ? 'bg-green-500 text-white active:bg-green-600' 
                                                  : 'bg-gray-100 text-gray-500 active:bg-gray-200'
                                              }`}
                                              title={isLocked ? 'Unlock score' : 'Lock score'}
                                            >
                                              {isLocked ? (
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                              ) : (
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                                </svg>
                                              )}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Slider - Only when not disabled */}
                                      {!isDisabled && (
                                        <div className="mt-2">
                                          <input
                                            type="range"
                                            min="0"
                                            max={criterion.percentage}
                                            value={currentScore}
                                            onChange={(e) => handleCriteriaScoreChange(candidate.id, criterion.id, e.target.value)}
                                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                            style={{
                                              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${currentScore * (100 / criterion.percentage)}%, #e5e7eb ${currentScore * (100 / criterion.percentage)}%, #e5e7eb 100%)`
                                            }}
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Locked / Submitted indicator */}
                                      {isSubmitted && !hasApprovedRequest('criteria', criterion.id) ? (
                                        <div className="mt-1 flex items-center gap-1 text-[10px] text-green-600">
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          <span>Submitted</span>
                                        </div>
                                      ) : !isCompleted && isLocked ? (
                                        <div className="mt-1 flex items-center gap-1 text-[10px] text-green-600">
                                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          <span>Score locked</span>
                                        </div>
                                      ) : null}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {/* Special Awards list for this candidate */}
                          {specialAwards.length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Special Awards</h4>
                              <div className="space-y-2">
                                {specialAwards.map(award => {
                                  const scoreKey = `${candidate.id}-${award.id}`
                                  const currentScore = awardScores[scoreKey] || 0
                                  const isLocked = isScoreLocked(scoreKey)
                                  const isCompleted = isAwardComplete(award.id)
                                  
                                  const isDisabled = isCompleted || isLocked || (isSubmitted && !hasApprovedRequest('award', award.id))
                                  const showLockBtn = !isCompleted && !(isSubmitted && !hasApprovedRequest('award', award.id))
                                  
                                  return (
                                    <div key={award.id} className="p-3 bg-white rounded-lg border border-gray-200">
                                      <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-semibold text-gray-900 truncate">{award.name}</p>
                                          <p className="text-xs text-gray-500">Max: 100 pts</p>
                                        </div>
                                        {!showLockBtn ? (
                                          <div className="text-right flex-shrink-0">
                                            <span className="text-base font-bold text-green-600">{currentScore}</span>
                                            <span className="text-xs text-gray-400">/100</span>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <input
                                              type="number"
                                              min="0"
                                              max="100"
                                              value={currentScore}
                                              onChange={(e) => handleAwardScoreChange(candidate.id, award.id, e.target.value)}
                                              disabled={isDisabled}
                                              className={`w-14 px-2 py-1.5 border rounded-lg text-center text-sm font-semibold transition-colors ${
                                                isDisabled 
                                                  ? 'bg-green-100 border-green-300 text-green-700 cursor-not-allowed' 
                                                  : 'border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                                              }`}
                                            />
                                            <button
                                              onClick={() => toggleScoreLock(scoreKey)}
                                              className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                                                isLocked 
                                                  ? 'bg-green-500 text-white active:bg-green-600' 
                                                  : 'bg-gray-100 text-gray-500 active:bg-gray-200'
                                              }`}
                                              title={isLocked ? 'Unlock score' : 'Lock score'}
                                            >
                                              {isLocked ? (
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                              ) : (
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                                </svg>
                                              )}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Slider - Only when not disabled */}
                                      {!isDisabled && (
                                        <div className="mt-2">
                                          <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={currentScore}
                                            onChange={(e) => handleAwardScoreChange(candidate.id, award.id, e.target.value)}
                                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                            style={{
                                              background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${currentScore}%, #e5e7eb ${currentScore}%, #e5e7eb 100%)`
                                            }}
                                          />
                                        </div>
                                      )}
                                      
                                      {/* Locked / Submitted indicator */}
                                      {isSubmitted && !hasApprovedRequest('award', award.id) ? (
                                        <div className="mt-1 flex items-center gap-1 text-[10px] text-green-600">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          <span>Submitted</span>
                                        </div>
                                      ) : !isCompleted && isLocked ? (
                                        <div className="mt-1 flex items-center gap-1 text-[10px] text-green-600">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          <span>Score locked</span>
                                        </div>
                                      ) : null}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {/* Submit Candidate Scores button */}
                          {!isSubmitted && (
                            <div className="pt-2 border-t border-gray-200 mt-4 space-y-2">
                              {!canSubmitCandidate(candidate.id) && (
                                <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                  <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                  <p className="text-xs text-amber-800 leading-relaxed">
                                    Lock all scores for this candidate before submitting. Click the lock icon next to each score to secure it.
                                  </p>
                                </div>
                              )}
                              <button
                                onClick={() => handleSubmitCandidateClick(candidate.id)}
                                disabled={submitting || !canSubmitCandidate(candidate.id)}
                                className="w-full py-2.5 bg-blue-500 text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:bg-blue-600"
                              >
                                {submitting ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Submit Candidate Scores
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recommendation Notification - Shows next recommended item to score */}
      {showRecommendation && recommendedItem && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 animate-slide-up">
          <div className={`rounded-xl shadow-2xl border-2 overflow-hidden ${
            recommendedItem.type === 'criteria' 
              ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-300' 
              : 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-300'
          }`}>
            {/* Header */}
            <div className={`px-4 py-2 ${
              recommendedItem.type === 'criteria' ? 'bg-blue-500' : 'bg-purple-500'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <span className="text-sm font-semibold text-white">Recommended Next</span>
                </div>
                <button 
                  onClick={dismissRecommendation}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  recommendedItem.type === 'criteria' ? 'bg-blue-500' : 'bg-purple-500'
                }`}>
                  {recommendedItem.type === 'criteria' ? (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${
                    recommendedItem.type === 'criteria' ? 'text-blue-600' : 'text-purple-600'
                  }`}>
                    {recommendedItem.type === 'criteria' ? 'Criteria' : 'Special Award'}
                  </p>
                  <h4 className="font-semibold text-gray-900 truncate">{recommendedItem.item.name}</h4>
                  {recommendedItem.item.description && (
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{recommendedItem.item.description}</p>
                  )}
                </div>
              </div>
              
              <button
                onClick={handleRecommendationClick}
                className={`w-full mt-4 py-2.5 rounded-lg font-medium text-sm text-white flex items-center justify-center gap-2 transition-colors ${
                  recommendedItem.type === 'criteria' 
                    ? 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700' 
                    : 'bg-purple-500 hover:bg-purple-600 active:bg-purple-700'
                }`}
              >
                <span>Go to {recommendedItem.item.name}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal - Compact */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md">
            {/* Modal Content */}
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Confirm Submission</h3>
                  <p className="text-xs text-gray-500">
                    {pendingSubmission?.type === 'candidate'
                      ? `Candidate #${candidates.find(c => c.id === pendingSubmission.id)?.candidate_number} - ${candidates.find(c => c.id === pendingSubmission.id)?.name}`
                      : pendingSubmission?.type === 'criteria' 
                      ? criteria.find(c => c.id === pendingSubmission.id)?.name
                      : specialAwards.find(a => a.id === pendingSubmission.id)?.name
                    }
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-red-800">
                  <strong>⚠️ Scores will be locked</strong> after submission and cannot be edited.
                </p>
              </div>

              {/* Scores Review */}
              <div className="mb-4 max-h-64 overflow-y-auto">
                <p className="text-xs font-medium text-gray-700 mb-2">Review your scores:</p>
                <div className="space-y-2">
                  {pendingSubmission?.type === 'candidate' ? (
                    <>
                      {criteria.filter(c => !c.is_locked).map(criterion => {
                        const score = criteriaScores[`${pendingSubmission.id}-${criterion.id}`] || 0
                        return (
                          <div key={criterion.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                            <span className="text-xs font-medium text-gray-900 truncate">{criterion.name} (Criteria)</span>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              <span className="text-sm font-bold text-blue-600">{score}</span>
                              <span className="text-xs text-gray-400">/{criterion.percentage}</span>
                            </div>
                          </div>
                        )
                      })}
                      {specialAwards.map(award => {
                        const score = awardScores[`${pendingSubmission.id}-${award.id}`] || 0
                        return (
                          <div key={award.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                            <span className="text-xs font-medium text-gray-900 truncate">{award.name} (Award)</span>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              <span className="text-sm font-bold text-purple-600">{score}</span>
                              <span className="text-xs text-gray-400">/100</span>
                            </div>
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    candidates.map(candidate => {
                      const scoreKey = `${candidate.id}-${pendingSubmission.id}`
                      const score = pendingSubmission?.type === 'criteria' 
                        ? criteriaScores[scoreKey] 
                        : awardScores[scoreKey]
                      const maxScore = pendingSubmission?.type === 'criteria'
                        ? criteria.find(c => c.id === pendingSubmission.id)?.percentage
                        : 100
                      
                      return (
                        <div key={candidate.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className="w-7 h-7 bg-blue-500 text-white rounded flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {candidate.candidate_number}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-900 truncate">{candidate.name}</p>
                              {candidate.representation && (
                                <p className="text-xs text-gray-500 truncate">{candidate.representation}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            <span className="text-sm font-bold text-blue-600">{score}</span>
                            <span className="text-xs text-gray-400">/{maxScore}</span>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleCancelSubmission}
                  disabled={submitting}
                  className="flex-1 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg active:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSubmission}
                  disabled={submitting}
                  className="flex-1 py-3 text-sm font-medium text-white bg-red-500 rounded-lg active:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Submitting</span>
                    </>
                  ) : (
                    <span>Confirm</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Scores Review Modal */}
      {showDetailedReview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-xl border-b border-blue-800 flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">My Submitted Scores</h2>
                <p className="text-sm text-blue-100 mt-1">{eventData.name}</p>
              </div>
              <button
                onClick={() => setShowDetailedReview(false)}
                className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto">
              {/* Criteria Scores */}
              {unlockedCriteria.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">My Criteria Scores</h3>
                  </div>
                  
                  {/* Note about locked criteria */}
                  {criteria.some(c => c.is_locked) && (
                    <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <p className="text-xs text-purple-800">
                          Only showing criteria you scored. Locked criteria are managed by the admin.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {unlockedCriteria.map((criterion) => {
                      if (!isCriteriaComplete(criterion.id)) return null
                      
                      return (
                        <div key={criterion.id} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                          <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-gray-900">{criterion.name}</h4>
                              <span className="text-sm text-gray-600">Max: {criterion.percentage}</span>
                            </div>
                            {criterion.description && (
                              <p className="text-xs text-gray-600 mt-1">{criterion.description}</p>
                            )}
                          </div>
                          
                          <div className="p-4">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="text-left py-2 px-2 font-semibold text-gray-700">#</th>
                                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Candidate</th>
                                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Representation</th>
                                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Score</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {candidates.map((candidate) => {
                                    const scoreKey = `${candidate.id}-${criterion.id}`
                                    const score = criteriaScores[scoreKey]
                                    
                                    return (
                                      <tr key={candidate.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                                        <td className="py-2 px-2">
                                          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded text-xs font-bold">
                                            {candidate.candidate_number}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 font-medium text-gray-900">{candidate.name}</td>
                                        <td className="py-2 px-2 text-gray-600 text-xs">{candidate.representation || '-'}</td>
                                        <td className="py-2 px-2 text-right">
                                          <span className="font-bold text-blue-600">{score}</span>
                                          <span className="text-xs text-gray-400">/{criterion.percentage}</span>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Special Awards Scores */}
              {specialAwards.length > 0 && specialAwards.some(a => isAwardComplete(a.id)) && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Special Award Scores</h3>
                  </div>

                  <div className="space-y-4">
                    {specialAwards.map((award) => {
                      if (!isAwardComplete(award.id)) return null
                      
                      return (
                        <div key={award.id} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                          <div className="bg-purple-50 border-b border-purple-200 px-4 py-3">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-gray-900">{award.name}</h4>
                              <span className="text-sm text-gray-600">Max: 100</span>
                            </div>
                            {award.description && (
                              <p className="text-xs text-gray-600 mt-1">{award.description}</p>
                            )}
                          </div>
                          
                          <div className="p-4">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-gray-200">
                                    <th className="text-left py-2 px-2 font-semibold text-gray-700">#</th>
                                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Candidate</th>
                                    <th className="text-left py-2 px-2 font-semibold text-gray-700">Representation</th>
                                    <th className="text-right py-2 px-2 font-semibold text-gray-700">Score</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {candidates.map((candidate) => {
                                    const scoreKey = `${candidate.id}-${award.id}`
                                    const score = awardScores[scoreKey]
                                    
                                    return (
                                      <tr key={candidate.id} className="border-b border-gray-100 hover:bg-purple-50 transition-colors">
                                        <td className="py-2 px-2">
                                          <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-500 text-white rounded text-xs font-bold">
                                            {candidate.candidate_number}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 font-medium text-gray-900">{candidate.name}</td>
                                        <td className="py-2 px-2 text-gray-600 text-xs">{candidate.representation || '-'}</td>
                                        <td className="py-2 px-2 text-right">
                                          <span className="font-bold text-purple-600">{score}</span>
                                          <span className="text-xs text-gray-400">/100</span>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-xl space-y-3">
              {/* Certification Section */}
              {isCertified ? (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 mb-1">✓ Scores Certified</h4>
                      <p className="text-sm text-green-800">
                        You certified your scores on {certificationTimestamp && new Date(certificationTimestamp).toLocaleString()}
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Admin has been notified of your certification.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 mb-1">Certify Your Scores</h4>
                      <p className="text-sm text-amber-800 mb-2">
                        By certifying, you confirm that all submitted scores are correct and final.
                      </p>
                      <p className="text-xs text-amber-700">
                        The admin will be notified once you certify your scores.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCertifyScores}
                    disabled={certifying}
                    className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {certifying ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Certifying...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                        Certify All Scores as Correct
                      </>
                    )}
                  </button>
                </div>
              )}
              
              <button
                onClick={() => setShowDetailedReview(false)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Request Modal */}
      {showChangeRequestModal && changeRequestData && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-2xl sm:rounded-t-xl border-b border-blue-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <div>
                  <h3 className="text-lg font-bold">Request Score Change</h3>
                  <p className="text-xs text-blue-100">{changeRequestData.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowChangeRequestModal(false)
                  setChangeRequestData(null)
                  setChangeRequestReason('')
                }}
                className="p-2 hover:bg-blue-800 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Important Information</p>
                    <p>Your request will be sent to the admin for review. You will be able to edit your scores only after the admin approves your request.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Change Request <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={changeRequestReason}
                  onChange={(e) => setChangeRequestReason(e.target.value)}
                  placeholder="Please explain why you need to change your scores..."
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Be specific about what you need to change and why
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowChangeRequestModal(false)
                    setChangeRequestData(null)
                    setChangeRequestReason('')
                  }}
                  disabled={requestingChange}
                  className="flex-1 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitChangeRequest}
                  disabled={requestingChange || !changeRequestReason.trim()}
                  className="flex-1 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {requestingChange ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Request Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-lg font-bold text-white">Need Help?</h2>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Describe what help you need and an admin will assist you as soon as possible.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  placeholder="Describe your issue or question..."
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Be as specific as possible to help the admin assist you better
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowHelpModal(false)
                    setHelpMessage('')
                  }}
                  disabled={sendingHelp}
                  className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitHelpRequest}
                  disabled={sendingHelp || !helpMessage.trim()}
                  className="flex-1 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingHelp ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Help Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JudgePanel